import { Trans } from '@lingui/macro'

import * as React from 'react'

import { TFCartOrder } from '~graphql/generated/clientGraphql'
import { Grid } from '~ui/components/layout/Box'
import { useToast } from '~ui/components/toasts/useToast'
import { Text } from '~ui/components/typography/Text/Text'

/**
 * Renders toast with the preset minimum order price information.
 *
 * Contains:
 *  - minimum order price value
 *  - information on its calculation
 *  - remaining amount to the minimum order price.
 */
export const useMinOrderPriceToast = (order: Nullable<TFCartOrder>) => {
  const [presentToast] = useToast()

  const presentMinOrderPriceToast = React.useCallback(() => {
    const minimalPrice = order?.minimalOrderPrice?.formattedValue

    if (!minimalPrice) {
      return
    }
    const minimalPriceRemainder = order?.minimalOrderPriceRemainder?.formattedValue

    presentToast({
      durationS: 10,
      type: `alert`,
      message: (textProps) => {
        // prettier-ignore
        const transportText = order?.isMinPriceWithTransport
          ? <Trans>including transport fee</Trans>
          : <Trans>without transport fee</Trans>

        // prettier-ignore
        const discountText = order?.isMinPriceAfterDiscounts
          ? <Trans>after discounts</Trans>
          : <Trans>before discounts</Trans>

        return (
          <Grid rowGap={`small`}>
            <Text {...textProps}>
              <Trans>Minimum order price is {minimalPrice}</Trans>
            </Text>

            <Text {...textProps} fontSize={`s14`}>
              <Trans>
                Minimum order price is price of products {transportText}, {discountText}.
              </Trans>{' '}
              <Text as={`span`} {...textProps} fontSize={`s14`} fontStyle={`bold`}>
                <Trans>Add additional items for at least {minimalPriceRemainder}.</Trans>
              </Text>
            </Text>
          </Grid>
        )
      },
    })
  }, [order, presentToast])

  return presentMinOrderPriceToast
}
